<template>
  <v-app>
    <v-main>
      <app-bar v-if="!isCalendarPage"></app-bar>

      <navigation-drawer v-if="isAuthenticated && !isCalendarPage"></navigation-drawer>

      <!-- This is essential -->
      <router-view></router-view>

      <!--
        Contextual add (+) floating action button.
        Pages register a single { icon: "mdi-plus", ... } action via
        useAppBarActions; AppBar.vue excludes it from the bar and we render it
        here as a bottom-right FAB. Its onClick/loading/color are preserved.
      -->
      <v-fab
        v-if="fabAction && !isCalendarPage"
        app
        location="bottom right"
        size="large"
        icon
        color="primary"
        :loading="fabAction.loading"
        @click="fabAction.onClick"
      >
        <!-- `icon` (boolean) keeps the FAB round; the +icon is rendered as a child
             because a string icon="mdi-plus" prop is suppressed by the tooltip slot. -->
        <v-icon>mdi-plus</v-icon>
        <v-tooltip activator="parent" location="left">{{ fabAction.tooltip }}</v-tooltip>
      </v-fab>

      <reviews-menu v-if="!isCalendarPage"></reviews-menu>
      <menu-menu v-if="!isCalendarPage"></menu-menu>
      <disputes-menu v-if="!isCalendarPage"></disputes-menu>
      <tasks-menu v-if="!isCalendarPage"></tasks-menu>

      <confirm-dialog></confirm-dialog>
      <v-chip v-if="isDev" class="position-fixed" style="bottom: 8px; right: 8px; z-index: 9999" size="x-small" color="primary" label>
        {{ name }} — {{ width }}px
      </v-chip>
    </v-main>
  </v-app>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { useApp } from "@/composables/useApp.js";
import { startTokenRefreshTimer, isAuthenticated as checkAuth } from "@/utils/api.js";
import { useUserPreferences } from "@/composables/useUserPreferences.js";
import { useAppBarActions } from "@/composables/useAppBarActions.js";
import AppBar from "@/components/AppBar.vue";
import NavigationDrawer from "@/components/NavigationDrawer.vue";
import SalesMenu from "@/components/SalesMenu.vue";
import ReviewsMenu from "@/components/ReviewsMenu.vue";
import MenuMenu from "@/components/FoodCostMenu.vue";
import DisputesMenu from "@/components/DisputesMenu.vue";
import TasksMenu from "@/components/TasksMenu.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";

const route = useRoute();
const { mobile, name, width, height } = useDisplay();
const { isAuthenticated } = useApp();

// Dev-only breakpoint/width chip (bottom-right). `import.meta.env.DEV` is true
// under `vite dev` and statically false in production builds, so the chip is
// tree-shaken out of prod entirely.
const isDev = import.meta.env.DEV;

// Calendar pages are standalone (no app bar or drawer)
const isCalendarPage = computed(() =>
  route.path.startsWith("/campaigns/calendar") ||
  (route.path.startsWith("/reviews-calendar") && !mobile.value)
);

const { loadPreferences } = useUserPreferences();

// The page-registered add ("+") action, rendered as the contextual FAB above.
// Mirrors the filter AppBar.vue uses to exclude it from the bar.
const { actions } = useAppBarActions();
const fabAction = computed(() =>
  actions.value.find((a) => a.show !== false && !a.chip && a.icon === "mdi-plus")
);

// Start token refresh timer and load user preferences on app load if authenticated
onMounted(() => {
  if (checkAuth()) {
    startTokenRefreshTimer();
    loadPreferences();
  }
});
</script>

<!-- Global (unscoped) app-shell hardening for mobile "solidity". Vuetify ships no
     utility for root overflow/overscroll, so this is the one place it lives.
     - overflow-x: clip  → a few px of horizontal overflow (v-row negative gutters,
       wide content) can no longer be dragged sideways, killing the horizontal
       "dance". `clip` (not `hidden`) does NOT create a scroll container, so
       position: sticky headers/filter rows keep working.
     - overscroll-behavior: none → stops the vertical rubber-band bounce that made
       scrolling feel unanchored. -->
<style>
html,
body {
  overflow-x: clip;
  overscroll-behavior: none;
}
</style>
